import PohlconApiService from './PohlconApiService';

/**
 * PohlconAuthService is a class that extends PohlconApiService and provides methods for authentication and authorization.
 */
export default class PohlconAuthService extends PohlconApiService {

  /**
   * OAuth2 endpoint configuration.
   */
  protected readonly endpoints = {
    oauth2Authorize: `${import.meta.env.VITE_API_URL}/identity/oauth/authorize`,
    oauth2Token: `${import.meta.env.VITE_API_URL}/identity/oauth/token`
  };

  /**
   * OAuth2 client configuration.
   */
  protected readonly apiClient = {
    clientId: import.meta.env.VITE_API_CLIENT_ID,
    redirectUri: import.meta.env.VITE_API_CLIENT_REDIRECT_URI || window.location.origin,
    scopes: import.meta.env.VITE_API_SCOPES
  }

  public constructor() {
    super('');
  }

  /**
   * Generates the authorization URL for the OAuth2 flow.
   * 
   * @param returnUrl - The return URL after successful authentication. Defaults to null.
   * @returns A promise that resolves to the authorization URL.
   */
  public async getAuthorizationUrl(returnUrl: string | null = null): Promise<string> {

    const pkceVerifier = this.generatePkceVerifier();
    const pkceChallenge = await this.generatePkceCodeChallenge(pkceVerifier);
    const state = this.generateState();

    this.storeState(state);
    this.storePkceVerifier(pkceVerifier);
    if (returnUrl) {
      this.storeReturnUrl(returnUrl);
    }

    const params = new URLSearchParams({
      client_id: this.apiClient.clientId,
      redirect_uri: this.apiClient.redirectUri,
      code_challenge: pkceChallenge,
      code_challenge_method: 'S256',
      response_type: 'code',
      state,
      scope: this.apiClient.scopes
    });

    return `${this.endpoints.oauth2Authorize}?${params.toString()}`;
  }


  /**
   * Exchanges the authorization code for an access token.
   * 
   * @param code - The authorization code received after successful authorization.
   * @returns A promise that resolves to the token response.
   */
  public async loginByAuthorizationCode(code: string, state: string): Promise<boolean> {

    const pkceVerifier = this.getPkceVerifier();
    const storedState = this.getState();

    if (state !== storedState) {
      return false;
    }
    try {
      const tokenResponse = await this.http.post(this.endpoints.oauth2Token, {
        grant_type: 'authorization_code',
        client_id: this.apiClient.clientId,
        code_verifier: pkceVerifier,
        code,
        redirect_uri: this.apiClient.redirectUri,
      });
      if (tokenResponse.data.access_token) {
        this.storeToken(tokenResponse.data.access_token);
        return true;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
    return false;

  }
   
  /**
   * Retrieves the account information.
   * @returns A Promise that resolves to the account data.
   */
  public async getAccount(): Promise<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;

  }> {
    const account = await this.http.get(`${import.meta.env.VITE_API_URL}/identity/auth/account`);
    return account.data;
  }

  /**
   * Logs out the user by sending a POST request to the logout endpoint.
   * Resets stored parameters after successful logout.
   * @returns A Promise that resolves to a boolean indicating the success of the logout operation.
   */
  public async logout(): Promise<boolean> {
    try {
      await this.http.post(`${import.meta.env.VITE_API_URL}/identity/auth/logout`);
    } catch (error) {
      console.log(error);
    }
    this.resetStoredParams();

    return true;
  }

  /**
   * Resets the stored parameters in the local storage.
   * Removes the bearer token, return URL, state, and PKCE from the local storage.
   */
  public resetStoredParams() {
    localStorage.removeItem(this.apiClient.clientId + '_bearer');
    localStorage.removeItem(this.apiClient.clientId + '_return_url');
    localStorage.removeItem(this.apiClient.clientId + '_state');
    localStorage.removeItem(this.apiClient.clientId + '_pkce');
  }

  /**
   * Stores the provided token in the local storage.
   * @param token - The token to be stored.
   */
  private storeToken(token: string) {
    localStorage.setItem(this.apiClient.clientId + '_bearer', token);
  }

  /**
   * Stores the return URL in the local storage.
   * 
   * @param returnUrl - The return URL to be stored.
   */
  private storeReturnUrl(returnUrl: string) {
    localStorage.setItem(this.apiClient.clientId + '_return_url', returnUrl);
  }

  /**
   * Retrieves the return URL from the local storage.
   * 
   * @returns The return URL.
   */
  public getReturnUrl() {
    return localStorage.getItem(this.apiClient.clientId + '_return_url');
  }

  /**
   * Stores the state in the local storage.
   * 
   * @param state - The state to be stored.
   */
  private storeState(state: string) {
    localStorage.setItem(this.apiClient.clientId + '_state', state);
  }

  /**
   * Retrieves the state from the local storage.
   * 
   * @returns The state.
   */
  private getState() {
    return localStorage.getItem(this.apiClient.clientId + '_state');
  }

  /**
   * Stores the PKCE verifier in the local storage.
   * 
   * @param verifier - The PKCE verifier to be stored.
   */
  private storePkceVerifier(verifier: string) {
    localStorage.setItem(this.apiClient.clientId + '_pkce', verifier);
  }

  /**
   * Retrieves the PKCE verifier from the local storage.
   * 
   * @returns The PKCE verifier.
   */
  private getPkceVerifier() {
    return localStorage.getItem(this.apiClient.clientId + '_pkce');
  }

  /**
   * Generates a PKCE (Proof Key for Code Exchange) verifier.
   * 
   * @returns The generated PKCE verifier.
   */
  private generatePkceVerifier(): string {
    return this.getRandomString(128);
  }

  /**
   * Generates a state for the OAuth2 flow.
   * @returns  The generated state.
   */
  private generateState(): string {
    return this.getRandomString(64);
  }

  private getRandomString(length: number): string {
    const charset = '0123456789abcdefghijklmnopqrstuvwxyz-._~';
    let data = '';
    const values = new Uint32Array(length);
    window.crypto.getRandomValues(values);
    for (let i = 0; i < length; i++) {
      data += charset[values[i] % charset.length];
    }
    return data;
  }

  /**
   * Generates a PKCE (Proof Key for Code Exchange) code challenge based on the provided code verifier.
   * 
   * @param codeVerifier - The code verifier to be used for generating the code challenge.
   * @returns A promise that resolves to the base64url encoded string representing the PKCE code challenge.
   */
  private async generatePkceCodeChallenge(codeVerifier: string): Promise<string> {
    // convert codeVerifier to ArrayBuffer
    const codeVerifierArray = new TextEncoder().encode(codeVerifier);
    // hash the codeVerifier using SHA-256
    const hashBuffer = await crypto.subtle.digest('SHA-256', codeVerifierArray);
    // convert ArrayBuffer to base64url encoded string
    const base64Encoded = btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(hashBuffer))))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    return base64Encoded;
  }

}
